import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowUpRight } from "lucide-react";
import PlanManager from "@/components/PlanManager";

function useCount(target, duration = 1200) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (target == null) return;
    let raf; let start;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / duration);
      setN(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return n;
}

export default function HeroMetric({ stats, onUpgrade, subscriptionActive }) {
  const total = useCount(stats?.recovered_month ?? 0);
  const dollars = Math.floor(total);
  const cents = Math.round((total - dollars) * 100).toString().padStart(2, "0");
  return (
    <Card
      className="relative overflow-hidden rounded-md p-6 sm:p-8 grain"
      style={{
        borderColor: "rgba(0,230,118,0.3)",
        boxShadow: "0 0 60px rgba(0,230,118,0.12), inset 0 0 0 1px rgba(0,230,118,0.15)",
        background: "linear-gradient(160deg, #101614 0%, #0F1112 60%, #101614 100%)",
      }}
      data-testid="hero-metric-card"
    >
      <div className="grid md:grid-cols-12 gap-6 items-center">
        <div className="md:col-span-7">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#00E676] live-dot" />
            <span className="font-mono text-[11px] uppercase tracking-widest text-[#00E676]">LIVE · RECOVERED THIS MONTH</span>
          </div>
          <div className="font-display font-extrabold tracking-tighter mt-2 text-6xl sm:text-7xl lg:text-8xl leading-none">
            <span className="text-[#00E676]" data-testid="hero-recovered-amount">
              ${dollars.toLocaleString()}
            </span>
            <span className="text-3xl sm:text-4xl align-top text-[#00C853]/70">.{cents}</span>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3 max-w-lg">
            <Breakdown label="Refunds landed" value={stats?.refunds_landed} testid="stat-refunds-landed" />
            <Breakdown label="Trials killed" value={stats?.trials_cancelled} testid="stat-trials-killed" />
            <Breakdown label="Class actions" value={stats?.class_actions_claimed} testid="stat-class-actions" />
          </div>
        </div>
        <div className="md:col-span-5">
          <div className="border border-[#282C30] rounded-md p-4 bg-[#0C0D0E]/70">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#606872]">STATUS</div>
            <div className="mt-1 text-sm text-[#F4F1EA]">
              {stats?.active_leaks ?? 0} active leaks · {stats?.receipts_scanned?.toLocaleString?.() ?? 0} receipts analyzed
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#606872] mt-3">PENDING REFUNDS</div>
            <div className="mt-1 text-2xl font-display font-bold tabular-nums" data-testid="stat-pending-refunds">
              ${(stats?.pending_refunds ?? 0).toFixed(2)}
            </div>
            {!subscriptionActive && (
              <Button className="btn-emerald w-full mt-4 h-10" onClick={onUpgrade} data-testid="upgrade-button">
                <Sparkles className="h-4 w-4 mr-1.5" /> Upgrade $12.99/mo · unlock everything
              </Button>
            )}
            {subscriptionActive && <PlanManager />}
          </div>
        </div>
      </div>
    </Card>
  );
}

function Breakdown({ label, value, testid }) {
  return (
    <div>
      <div className="font-display text-xl sm:text-2xl font-bold tabular-nums" data-testid={testid}>
        ${(value ?? 0).toFixed(2)}
      </div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#606872] mt-0.5">{label}</div>
    </div>
  );
}
